import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { IImpact } from "@/pages/dashboard/utils/types";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export function useGetCommuteYears() {
  return useQuery({
    queryKey: ["employees", "commute"],
    queryFn: () =>
      axios.get<YearStatus[]>("/employees/commute/years").then((result) => result.data),
  });
}

export interface Commute {
  id: string;
  employee_id: string;
  year: number;
  country: string;
  travel_mode: string;
  travel_mode_id: number;
  distance: number;
  owned: boolean;
  dataset_id?: number;
  impact: IImpact;
}

export function useGetCommute({
  year,
  pagination,
  filter,
  search,
  sort,
  enabled,
}: {
  year?: number;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["employees", "commute", year, pagination, filter, sort, search],
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<Commute>>(`/employees/commute/${year}`, {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export function useDeleteCommute() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ year }: { year?: number }) =>
      axios.delete(`/employees/commute`, {
        params: {
          year: year,
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["employees", "commute"]);
      smartQueryClient.invalidateQueries(["employees", "travel_modes"]);
    },
  });
}

export function useDeleteCommuteAll() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: () => axios.delete(`/employees/commute`),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["employees", "commute"]);
      smartQueryClient.invalidateQueries(["employees", "travel_modes"]);
    },
  });
}

export const exportCommute = async ({ year }: { year?: number }): Promise<string> => {
  const { data } = await axios.get<{ url: string }>(`/employees/commute/export`, {
    params: { ...(year ? { year } : {}) },
  });

  return data.url;
};
